// Pagina di ritorno dopo il pagamento (o dopo l'invio di una richiesta d'ordine).
// NON marca nulla come pagato: mostra lo stato VERIFICATO dal server
// (`payment_state`), che aggiornano il webhook Stripe o la riconciliazione.
// Interroga l'ordine finché lo stato non è definitivo, con un tempo massimo:
// niente spinner infinito, niente pagina bianca (R-64, R-73, R-75).
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch, ApiError } from "../api/client";
import { paymentErrorKey } from "../api/paymentError";
import { ga4 } from "../analytics/ga4";
import { analyticsIds } from "../analytics/tracker";
import { pathFor } from "../app/paths";
import { useCart } from "../cart/useCart";
import {
  clearCheckoutIntent,
  isOrderId,
  paymentStateOf,
  pendingCheckout,
  type PaymentState,
} from "../checkout/checkoutState";

type OrderStatus = "PENDING" | "PAID" | "SHIPPED" | "DELIVERED" | "CANCELLED";

interface OrderItem {
  product_id: string | null;
  product_title: string;
  quantity: number;
  price_cents: number;
}

interface Order {
  id: string;
  order_number: number | null;
  status: OrderStatus;
  payment_status: string | null;
  total_cents: number;            // LORDO (IVA inclusa)
  currency: string;
  // Ripartizione IVA (null sugli ordini pre-IVA)
  taxable_cents?: number | null;
  vat_rate?: number | null;
  vat_cents?: number | null;
  discount_code?: string | null;
  items: OrderItem[];
  // Stato verificato dal server (assente su un backend precedente).
  payment_state?: PaymentState;
  can_pay_online?: boolean;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

/** Acquisto concluso e pagato. */
const PAID_STATES: PaymentState[] = ["paid", "shipped", "delivered"];
/** Stati in cui la conferma può ancora arrivare: si continua a interrogare. */
const WAITING_STATES: PaymentState[] = ["awaiting_payment", "processing"];
/** Stati da cui il cliente può riprovare il pagamento. */
const RETRY_STATES: PaymentState[] = ["failed", "expired"];

/** Titolo, testo e stato breve per ogni stato verificato. */
const TEXTS: Record<PaymentState, { title: string; body: string; status: string }> = {
  paid: { title: "titleConfirmed", body: "bodyConfirmed", status: "statusConfirmed" },
  shipped: { title: "titleConfirmed", body: "bodyConfirmed", status: "statusShipped" },
  delivered: { title: "titleConfirmed", body: "bodyConfirmed", status: "statusDelivered" },
  request_received: { title: "titleRequest", body: "bodyRequest", status: "statusRequest" },
  on_hold: { title: "titleOnHold", body: "bodyOnHold", status: "statusOnHold" },
  processing: { title: "titleProcessing", body: "bodyProcessing", status: "statusProcessing" },
  failed: { title: "titleFailed", body: "bodyFailed", status: "statusFailed" },
  expired: { title: "titleExpired", body: "bodyExpired", status: "statusExpired" },
  cancelled: { title: "titleCancelled", body: "bodyCancelled", status: "statusCancelled" },
  awaiting_payment: { title: "titlePending", body: "bodyPending", status: "statusPending" },
};

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(cents / 100);
}

export function CheckoutSuccess() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const { clearCart } = useCart();
  const [params] = useSearchParams();
  const rawOrderId = params.get("order_id");
  // Un order_id dall'URL entra in un percorso API solo se è un UUID (R-75).
  const orderId = isOrderId(rawOrderId) ? rawOrderId : null;

  const [order, setOrder] = useState<Order | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [loadError, setLoadError] = useState<"not_found" | "failed" | null>(null);
  // Cambia per far ripartire le letture (dopo un «già pagato» al riprova).
  const [pollRound, setPollRound] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState("");

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let timer: number | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const o = await apiFetch<Order>(`/api/v1/shop/orders/${orderId}`);
        if (cancelled) return;
        setOrder(o);
        setLoadError(null);
        if (!WAITING_STATES.includes(paymentStateOf(o))) return; // stato definitivo
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          setLoadError("not_found");
          return;
        }
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setLoadError("failed");
          return;
        }
        // Errore transitorio (rete, 5xx): si riprova fino al tempo massimo.
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [orderId, pollRound]);

  const state: PaymentState | null = order ? paymentStateOf(order) : null;

  // Acquisto concluso o richiesta registrata: il carrello e l'intento non
  // servono più. Solo su stato VERIFICATO dal server, mai sull'URL. Il carrello
  // si svuota solo se è questa scheda ad aver avviato QUEL pagamento: riaprire
  // dalla cronologia l'esito di un vecchio ordine non deve cancellare il carrello nuovo.
  useEffect(() => {
    if (!order || !state) return;
    const concluded = PAID_STATES.includes(state) || state === "request_received" || state === "on_hold";
    if (concluded) {
      if (pendingCheckout()?.orderId === order.id) clearCart();
      clearCheckoutIntent(order.id);
    } else if (state === "cancelled") {
      clearCheckoutIntent(order.id);
    }
  }, [order, state, clearCart]);

  // GA4 purchase: una sola volta per ordine pagato, solo con il consenso. Lo
  // stato arriva dal backend, non dall'URL (falsificabile). La chiave
  // anti-duplicato in localStorage regge anche la riapertura in una scheda nuova
  // e si scrive solo se l'evento parte davvero: senza consenso resta in attesa
  // e parte se il consenso arriva (ga4.flushAfterConsent).
  useEffect(() => {
    if (!order || state !== "paid") return;
    const txn = order.order_number != null ? String(order.order_number) : order.id;
    ga4.purchase({
      transaction_id: txn,
      valueCents: order.total_cents,   // LORDO: coincide con l'importo addebitato da Stripe
      currency: order.currency || "EUR",
      taxCents: order.vat_cents ?? null,   // valore dell'ordine, non ricalcolato
      coupon: order.discount_code ?? null,
      items: (order.items || []).map((it) => ({
        item_id: it.product_id || it.product_title,
        item_name: it.product_title,
        price: (it.price_cents || 0) / 100,
        quantity: it.quantity,
      })),
      dedupeKey: `gb_ga4_purchase_${txn}`,
    });
  }, [order, state]);

  const handleRetry = async () => {
    if (!orderId || retrying) return;
    setRetrying(true);
    setRetryError("");
    try {
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        { method: "POST", body: JSON.stringify({ order_id: orderId, locale, ...analyticsIds() }) },
      );
      window.location.assign(session.checkout_url);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === "already_paid") {
        // Pagato nel frattempo: si rilegge lo stato, con un tempo massimo nuovo.
        setTimedOut(false);
        setPollRound((n) => n + 1);
      } else {
        setRetryError(t(paymentErrorKey(err)));
      }
      setRetrying(false);
    }
  };

  const waiting = !!orderId && !loadError && (!state || WAITING_STATES.includes(state)) && !timedOut;
  const texts = state ? TEXTS[state] : null;

  let title: string;
  let body: string;
  if (!orderId) {
    title = t("checkout.success.titleUnknown");
    body = rawOrderId ? t("checkout.success.invalidOrder") : t("checkout.success.bodyNoOrder");
  } else if (loadError === "not_found") {
    title = t("checkout.success.titleUnknown");
    body = t("checkout.success.invalidOrder");
  } else if (!texts) {
    // Nessuna risposta ancora: in attesa, oppure il tempo è scaduto senza dati.
    title = timedOut || loadError ? t("checkout.success.titleUnknown") : t("checkout.success.titlePending");
    body = timedOut || loadError ? t("checkout.success.loadError") : t("checkout.success.bodyPending");
  } else {
    title = t(`checkout.success.${texts.title}`);
    body = state === "awaiting_payment" && timedOut
      ? t("checkout.success.bodyTimeout")
      : t(`checkout.success.${texts.body}`);
  }

  const mark: MarkKind = waiting
    ? "wait"
    : state && (PAID_STATES.includes(state) || state === "request_received")
      ? "ok"
      : state && (state === "processing" || state === "on_hold" || state === "awaiting_payment")
        ? "soft"
        : "warn";
  const canRetry = !!order && !!state && RETRY_STATES.includes(state) && order.can_pay_online === true;
  const totalItems = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
  // Numero ordine progressivo (nuovi ordini) o codice legacy (UUID) per i vecchi.
  const shortId = order
    ? `#${order.order_number != null ? order.order_number : order.id.slice(0, 8).toUpperCase()}`
    : "";

  return (
    <div style={{ minHeight: "calc(100vh - 200px)", background: cfg.color_light_bg, padding: "64px 24px" }}>
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          padding: "56px 40px 40px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
          textAlign: "center",
        }}
      >
        <StatusMark kind={mark} color={cfg.color_primary} warnColor={cfg.color_accent} />

        <h1
          style={{
            fontFamily: cfg.font_heading,
            fontSize: 32,
            color: cfg.color_dark,
            margin: "28px 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h1>

        <p role="status" aria-live="polite" style={{ color: cfg.color_muted, fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
          {body}
        </p>

        {order && state && texts && (
          <div
            style={{
              borderTop: `1px solid ${cfg.color_light_bg}`,
              borderBottom: `1px solid ${cfg.color_light_bg}`,
              padding: "20px 0",
              margin: "0 0 32px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              textAlign: "left",
            }}
          >
            <SummaryItem label={t("checkout.success.order")} value={shortId} cfg={cfg} />
            <SummaryItem
              label={t("checkout.success.items")}
              value={`${totalItems} ${totalItems === 1 ? t("checkout.success.unit") : t("checkout.success.unitPlural")}`}
              cfg={cfg}
            />
            {order.vat_cents != null && order.vat_rate ? (
              <>
                <SummaryItem
                  label={t("cart.taxableBase")}
                  value={formatMoney(order.taxable_cents ?? 0, order.currency)}
                  cfg={cfg}
                />
                <SummaryItem
                  label={t("cart.vat", { rate: order.vat_rate })}
                  value={formatMoney(order.vat_cents, order.currency)}
                  cfg={cfg}
                />
                <SummaryItem
                  label={t("cart.totalInclVat")}
                  value={formatMoney(order.total_cents, order.currency)}
                  cfg={cfg}
                  emphasize
                />
              </>
            ) : (
              <SummaryItem
                label={t("checkout.success.totalLabel")}
                value={formatMoney(order.total_cents, order.currency)}
                cfg={cfg}
                emphasize
              />
            )}
            <SummaryItem
              label={t("checkout.success.statusLabel")}
              value={t(`checkout.success.${texts.status}`)}
              cfg={cfg}
              statusColor={mark === "ok" ? cfg.color_primary : cfg.color_accent}
            />
          </div>
        )}

        {retryError && (
          <div
            role="alert"
            style={{ background: "#fff5f5", color: "#b91c1c", padding: "12px 16px", borderRadius: 8, fontSize: 13, marginBottom: 20, textAlign: "left" }}
          >
            {retryError}
          </div>
        )}

        {canRetry && (
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              style={{
                background: cfg.color_primary,
                color: "#fff",
                border: "none",
                padding: "14px 32px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.02em",
                cursor: retrying ? "wait" : "pointer",
                opacity: retrying ? 0.7 : 1,
              }}
            >
              {retrying ? t("checkout.cancel.retrying") : t("checkout.success.retryPayment")}
            </button>
          </div>
        )}

        <Link
          to={`/${locale || "it"}/account`}
          style={{
            display: "inline-block",
            background: cfg.color_dark,
            color: "#fff",
            padding: "14px 36px",
            borderRadius: 999,
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          {t("checkout.success.ctaOrders")}
        </Link>

        <div style={{ marginTop: 20 }}>
          <Link
            to={pathFor("collections", locale)}
            style={{ color: cfg.color_muted, fontSize: 13, textDecoration: "none" }}
          >
            ← {t("checkout.success.ctaShop")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function SummaryItem({
  label,
  value,
  cfg,
  emphasize,
  statusColor,
}: {
  label: string;
  value: string;
  cfg: ReturnType<typeof useSiteConfig>;
  emphasize?: boolean;
  statusColor?: string;
}) {
  return (
    <div>
      <div
        style={{
          color: cfg.color_muted,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: statusColor || cfg.color_dark,
          fontSize: emphasize ? 20 : 15,
          fontWeight: emphasize ? 600 : 500,
          fontFamily: emphasize ? cfg.font_heading : "inherit",
        }}
      >
        {value}
      </div>
    </div>
  );
}

type MarkKind = "wait" | "ok" | "soft" | "warn";

function StatusMark({ kind, color, warnColor }: { kind: MarkKind; color: string; warnColor: string }) {
  const size = 72;
  if (kind === "wait") {
    return (
      <div
        data-testid="status-wait"
        style={{
          width: size,
          height: size,
          margin: "0 auto",
          borderRadius: "50%",
          border: `3px solid ${color}33`,
          borderTopColor: color,
          animation: "gb-spin 0.9s linear infinite",
        }}
      >
        <style>{`@keyframes gb-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (kind === "warn") {
    // Esito da gestire (non riuscito, scaduto, annullato, link non valido):
    // un segno di spunta qui direbbe il contrario di quello che è successo.
    return (
      <svg data-testid="status-warn" width={size} height={size} viewBox="0 0 72 72" style={{ display: "block", margin: "0 auto" }}>
        <circle cx="36" cy="36" r="34" fill={warnColor} opacity="0.12" />
        <circle cx="36" cy="36" r="26" fill={warnColor} />
        <rect x="34" y="22" width="4" height="18" rx="2" fill="#fff" />
        <circle cx="36" cy="48" r="2.6" fill="#fff" />
      </svg>
    );
  }
  const filled = kind === "ok";
  return (
    <svg data-testid={filled ? "status-ok" : "status-soft"} width={size} height={size} viewBox="0 0 72 72" style={{ display: "block", margin: "0 auto" }}>
      <circle cx="36" cy="36" r="34" fill={color} opacity={filled ? "0.12" : "0.06"} />
      <circle cx="36" cy="36" r="26" fill={filled ? color : `${color}66`} />
      <path
        d="M24 36.5 L33 45.5 L49 28.5"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
